import { motion } from "framer-motion";
import { Check, Minus } from "lucide-react";
import { fadeUp, staggerChild, staggerContainer } from "../lib/motion";

const fit = [
  "You already have audience traction",
  "Your content generates trust and demand",
  "Attention is there, but the backend is underbuilt",
  "You know there is more revenue inside the business than you're capturing",
  "You want a high-level operator who thinks strategically and builds practically",
  "You want to stay in your zone of genius while someone strengthens the business behind you",
];

const notFit = [
  "You're still trying to figure out your niche",
  "You don't yet have audience trust or market pull",
  "You want a generic marketing service",
  "You need basic content support more than backend infrastructure",
  "You're looking for low-touch, low-commitment help",
];

export const WhoFor = () => {
  return (
    <section
      id="who-its-for"
      data-testid="who-section"
      className="relative py-24 md:py-32 lg:py-44 px-6 md:px-12 lg:px-20 max-w-[1500px] mx-auto"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <motion.div {...fadeUp} className="flex items-center gap-3 mb-6">
            <span className="eyebrow">05 — Who It&rsquo;s For</span>
            <span className="block w-8 h-px bg-[#282930]" />
          </motion.div>
          <motion.h2
            {...fadeUp}
            className="font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-tight"
          >
            Built for creators with{" "}
            <span className="italic text-[#C9A26B]">momentum</span>—not
            beginners looking for direction.
          </motion.h2>
          <motion.p {...fadeUp} className="mt-8 text-[#A8A39D] max-w-[44ch] leading-relaxed">
            We do our best work with creators who already have signal. What
            they need now is structure.
          </motion.p>
        </div>

        <div className="lg:col-span-7 space-y-14">
          <div>
            <div className="flex items-baseline justify-between mb-6">
              <p className="eyebrow text-[#C9A26B]">A fit if</p>
              <span className="text-xs text-[#A8A39D] tabular-nums">{fit.length} signals</span>
            </div>
            <motion.ul {...staggerContainer} className="space-y-0">
              {fit.map((f) => (
                <motion.li
                  {...staggerChild}
                  key={f}
                  className="group py-4 border-t border-[#1D1E24] last:border-b flex items-start gap-4 text-[#F4EFE6] hover:bg-[#16171B] transition-colors px-1"
                >
                  <Check size={16} className="mt-1 text-[#C9A26B] flex-shrink-0" strokeWidth={1.5} />
                  <span>{f}</span>
                </motion.li>
              ))}
            </motion.ul>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-6">
              <p className="eyebrow text-[#A8A39D]">Not for you if</p>
              <span className="text-xs text-[#A8A39D] tabular-nums">{notFit.length} signals</span>
            </div>
            <motion.ul {...staggerContainer} className="space-y-0">
              {notFit.map((f) => (
                <motion.li
                  {...staggerChild}
                  key={f}
                  className="py-4 border-t border-[#1D1E24] last:border-b flex items-start gap-4 text-[#A8A39D]"
                >
                  <Minus size={16} className="mt-1 text-[#A8A39D]/60 flex-shrink-0" strokeWidth={1.5} />
                  <span>{f}</span>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoFor;
